import {
  BarChart2,
  BedDouble,
  Briefcase,
  TrendingUp,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  CreditCard,
  FileImage,
  FileSearch,
  FileText,
  FlaskConical,
  Heart,
  HeartPulse,
  LayoutDashboard,
  LayoutGrid,
  Lock,
  MapPin,
  MessageSquare,
  Receipt,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Store,
  TestTube2,
  UserCheck,
  UserCog,
  UserCircle2,
  Users,
} from "lucide-react";
import type { CapabilitiesDto, CapabilityModuleKey } from "@/features/capabilities/types";
import type { DashboardNavBadgeKey } from "@/features/dashboard/hooks/useNavBadges";
import type { BusinessRole, ProfileRole, SystemRole } from "@/types/roles";

export interface NavItem {
  label:         string;
  to:            string;
  icon:          JSX.Element;
  systemRoles?:  readonly SystemRole[];
  profileRoles?: readonly ProfileRole[];
  businessRoles?:readonly BusinessRole[];
  moduleKey?:    CapabilityModuleKey;
  minLevel?:     number;
  badgeKey?:     DashboardNavBadgeKey;
}

export const HYBRID_BUSINESS_PATHS = [
  "/app/business",
  "/app/corporate",
  "/app/finance",
  "/app/inventory",
  "/app/seller",
  "/app/service-hub",
  "/app/wallet",
];

// ═══════════════════════════════════════════════════════════════════
// PATIENT_NAV — Kushtetuta §II.1: Roli i Pacientit
// Dashboard personal, kujdesi shëndetësor dhe financa
// ═══════════════════════════════════════════════════════════════════
export const PATIENT_NAV: NavItem[] = [
  { label: "Dashboard",        to: "/app/patient",                 icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: "Kërko Mjekun",     to: "/search",                      icon: <Search className="h-4 w-4" /> },
  { label: "Wallet",           to: "/app/wallet",   moduleKey: "wallet", icon: <CreditCard className="h-4 w-4" /> },
  { label: "Terminet e Mia",   to: "/app/patient/appointments", moduleKey: "appointments", badgeKey: "appointments", icon: <CalendarDays className="h-4 w-4" /> },
  { label: "Monitorimi Im",    to: "/app/patient/monitoring", moduleKey: "monitoring",    icon: <HeartPulse className="h-4 w-4" /> },
  { label: "Familja Ime",      to: "/app/patient/family",    moduleKey: "family_care", badgeKey: "family_requests", icon: <Users className="h-4 w-4" /> },
  { label: "Dashboard Familjar", to: "/app/family/dashboard", moduleKey: "family_care", badgeKey: "family_requests", icon: <Users className="h-4 w-4" /> },
  { label: "Historiku Klinik", to: "/app/patient/history",         icon: <FileText className="h-4 w-4" /> },
  { label: "Sigurimet",        to: "/app/insurance",               icon: <ShieldCheck className="h-4 w-4" /> },
  { label: "Ngjarjet",         to: "/app/events",                  icon: <CalendarRange className="h-4 w-4" /> },
  { label: "Favoritet",        to: "/app/favorites", badgeKey: "favorites", icon: <Heart className="h-4 w-4" /> },
  { label: "Shporta",          to: "/app/cart", badgeKey: "cart", icon: <ShoppingCart className="h-4 w-4" /> },
  { label: "Medimarket",       to: "/commerce",                    icon: <ShoppingBag className="h-4 w-4" /> },
  { label: "Porositë e Mia",  to: "/app/orders", badgeKey: "orders", icon: <Receipt className="h-4 w-4" /> },
  { label: "Mesazhet",         to: "/app/messages",                moduleKey: "chat", badgeKey: "messages", icon: <MessageSquare className="h-4 w-4" /> },
  { label: "Dashboard Korporativ", to: "/app/corporate", businessRoles: ["owner"], icon: <Building2 className="h-4 w-4" /> },
  { label: "Llogaria",         to: "/app/account",                 icon: <UserCog className="h-4 w-4" /> },
];

// ═══════════════════════════════════════════════════════════════════
// BASIC_NAV — llogari fillestare pa endpoint-e patient-only.
export const BASIC_NAV: NavItem[] = [
  { label: "Dashboard",        to: "/app/dashboard",               icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: "Kërko Mjekun",     to: "/search",                      icon: <Search className="h-4 w-4" /> },
  { label: "Terminet e Mia",   to: "/app/appointments", moduleKey: "appointments", badgeKey: "appointments", icon: <CalendarDays className="h-4 w-4" /> },
  { label: "Blogu im",         to: "/app/blog",                    icon: <FileText className="h-4 w-4" /> },
  { label: "Favoritet",        to: "/app/favorites", badgeKey: "favorites", icon: <Heart className="h-4 w-4" /> },
  { label: "Shporta",          to: "/app/cart", badgeKey: "cart", icon: <ShoppingCart className="h-4 w-4" /> },
  { label: "Medimarket",       to: "/commerce",                    icon: <ShoppingBag className="h-4 w-4" /> },
  { label: "Mesazhet",         to: "/app/messages",                moduleKey: "chat", badgeKey: "messages", icon: <MessageSquare className="h-4 w-4" /> },
  { label: "Llogaria",         to: "/app/account",                 icon: <UserCog className="h-4 w-4" /> },
];

// CLINICIAN_NAV — Kushtetuta §II.2: Roli i Klinicistit
// Agjenda klinike + workbench; BEZ Wallet dhe BEZ Search personal
// ═══════════════════════════════════════════════════════════════════
export const CLINICIAN_NAV: NavItem[] = [
  { label: "Agjenda",          to: "/app/professional",            icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: "Workbench",        to: "/app/professional/reports", badgeKey: "reports", icon: <ClipboardList className="h-4 w-4" /> },
  { label: "Blogu im",         to: "/app/blog",                    icon: <FileText className="h-4 w-4" /> },
  { label: "Raportet Mjekësore", to: "/app/reports",                icon: <FileText className="h-4 w-4" /> },
  { label: "Profili im (CV)",  to: "/app/professional/settings",   icon: <UserCircle2 className="h-4 w-4" /> },
  { label: "Regjistro Biznesin", to: "/app/business/register",      icon: <Building2 className="h-4 w-4" />, profileRoles: ["doctor", "specialist"] as const },
  { label: "Oraret",           to: "/app/professional/schedule", moduleKey: "appointments", badgeKey: "appointments", icon: <CalendarDays className="h-4 w-4" /> },
  { label: "Monitorimi",       to: "/app/doctor/monitoring",   moduleKey: "monitoring",    icon: <HeartPulse className="h-4 w-4" /> },
  { label: "Laboratori",       to: "/app/laboratory",              moduleKey: "laboratory", icon: <FlaskConical className="h-4 w-4" /> },
  { label: "Rezultatet",       to: "/app/laboratory/results",      moduleKey: "laboratory", icon: <TestTube2 className="h-4 w-4" /> },
  { label: "Imazheritë",       to: "/app/imaging",                 icon: <FileImage className="h-4 w-4" /> },
  { label: "Ngjarjet",         to: "/app/events",                  icon: <CalendarRange className="h-4 w-4" /> },
  { label: "Ofertat e Punës",  to: "/app/jobs",                    icon: <Briefcase className="h-4 w-4" /> },
  { label: "Mesazhet",         to: "/app/messages",                moduleKey: "chat", badgeKey: "messages", icon: <MessageSquare className="h-4 w-4" /> },
  { label: "Llogaria",         to: "/app/account",                 icon: <UserCog className="h-4 w-4" /> },
];

// ═══════════════════════════════════════════════════════════════════
// TECHNICIAN_NAV — Kushtetuta §II.2b: Roli i Laborantit
// 5 items ekskluzivisht laboratorike; BEZ mjekësi klinike
// ═══════════════════════════════════════════════════════════════════
export const TECHNICIAN_NAV: NavItem[] = [
  { label: "Panel Laborantit", to: "/app/laboratory/admin",    moduleKey: "laboratory", badgeKey: "lab_orders", icon: <ClipboardList className="h-4 w-4" /> },
  { label: "Laboratori",       to: "/app/laboratory",          moduleKey: "laboratory", icon: <FlaskConical   className="h-4 w-4" /> },
  { label: "Rezultatet",       to: "/app/laboratory/results",  moduleKey: "laboratory", icon: <TestTube2      className="h-4 w-4" /> },
  { label: "Mesazhet",         to: "/app/messages",            moduleKey: "chat", badgeKey: "messages", icon: <MessageSquare  className="h-4 w-4" /> },
  { label: "Llogaria",        to: "/app/account",             icon: <UserCog        className="h-4 w-4" /> },
];

export const DOCTOR_SPECIALIST_NAV: NavItem[] = [
  { label: "Dashboard", to: "/app/professional", icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: "Workbench", to: "/app/professional/reports", badgeKey: "reports", icon: <ClipboardList className="h-4 w-4" /> },
  { label: "Profili im (CV)", to: "/app/professional/settings", icon: <UserCircle2 className="h-4 w-4" /> },
  { label: "Blogu im", to: "/app/blog", icon: <FileText className="h-4 w-4" /> },
  { label: "Schedule", to: "/app/professional/schedule", moduleKey: "appointments", badgeKey: "appointments", icon: <CalendarDays className="h-4 w-4" /> },
  { label: "Regjistro Biznesin", to: "/app/business/register", icon: <Building2 className="h-4 w-4" />, profileRoles: ["doctor", "specialist"] as const },
  { label: "Service Hub", to: "/app/service-hub", icon: <LayoutGrid className="h-4 w-4" />, businessRoles: ["owner", "staff", "technical", "doctor"] as const },
];

export const TECHNICIAN_WORKBENCH_NAV: NavItem[] = [
  { label: "Lab Admin", to: "/app/laboratory/admin", moduleKey: "laboratory", badgeKey: "lab_orders", icon: <ClipboardList className="h-4 w-4" /> },
  { label: "Profili im (CV)", to: "/app/professional/settings", icon: <UserCircle2 className="h-4 w-4" /> },
  { label: "Regjistro Biznesin", to: "/app/business/register", icon: <Building2 className="h-4 w-4" /> },
  { label: "Service Hub", to: "/app/service-hub", icon: <LayoutGrid className="h-4 w-4" /> },
];

export const STUDENT_NAV: NavItem[] = [
  { label: "Profile Only", to: "/app/professional/profile", icon: <UserCircle2 className="h-4 w-4" /> },
];

// Pronari ka dritaren e vet të punës, pa zhurmë pacient/mjek.
export const OWNER_WORKSPACE_NAV: NavItem[] = [
  { label: "Dashboard Korporativ",  to: "/app/corporate",   icon: <Building2   className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Klinika Ime",            to: "/app/business/manage", badgeKey: "staff_requests", icon: <Building2 className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Portofoli",              to: "/app/wallet",     icon: <CreditCard className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Faturat e Klinikës",     to: "/app/business/billing", badgeKey: "billing_invoices", icon: <Receipt className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Departamentet",           to: "/app/business/manage?tab=departments", badgeKey: "departments", minLevel: 2, icon: <Building2 className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Inventari",              to: "/app/inventory",   moduleKey: "inventory", minLevel: 2, icon: <LayoutGrid className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Inteligjenca Financiare", to: "/app/finance",   moduleKey: "finance", minLevel: 2, icon: <TrendingUp  className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Shtretërit",              to: "/app/hospital/beds", minLevel: 3, icon: <BedDouble className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Pranimet",                to: "/app/hospital/admissions", minLevel: 3, icon: <ClipboardList className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Dyqani im",             to: "/app/business/commerce", badgeKey: "orders", icon: <Store className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Service Hub",           to: "/app/service-hub", icon: <LayoutGrid  className="h-4 w-4" />, businessRoles: ["owner"] },
];

// ═══════════════════════════════════════════════════════════════════
// ADMIN_NAV — Kushtetuta §II.3 & §II.4: Roli i Adminit/Superadminit
// Governance & Platform Health; ASNJË element klinik ose financiar personal
// ASNJË: Wallet, Invoices, Seller, Commerce, Search personal — janë BLLOKUAR
// ═══════════════════════════════════════════════════════════════════
export const ADMIN_NAV: NavItem[] = [
  { label: "Control Room",       to: "/app/admin",              icon: <Shield className="h-4 w-4" /> },
  { label: "Verifikimi Mjekëve", to: "/app/admin/users", badgeKey: "admin_approvals", icon: <UserCheck className="h-4 w-4" /> },
  { label: "Moderimi",           to: "/app/admin/moderation",   icon: <FileText className="h-4 w-4" /> },
  { label: "Siguria",            to: "/app/admin/security", badgeKey: "security_events", icon: <Lock className="h-4 w-4" /> },
  { label: "Statistikat",        to: "/app/admin/analytics",    moduleKey: "analytics", minLevel: 2, icon: <BarChart2 className="h-4 w-4" /> },
  { label: "Çmimet & Planet",       to: "/app/pricing",               icon: <Receipt    className="h-4 w-4" /> },
  { label: "Faturimi Global",        to: "/app/admin/billing", badgeKey: "billing_invoices", icon: <CreditCard className="h-4 w-4" />, systemRoles: ["admin", "superadmin"] as const },
  { label: "Rrjeti i Organizatave",  to: "/app/admin/organizations",   icon: <Building2  className="h-4 w-4" />, systemRoles: ["admin", "superadmin"] as const },
  { label: "Menaxhimi i Medias",     to: "/app/admin/media",           icon: <FileImage  className="h-4 w-4" />, systemRoles: ["admin", "superadmin"] as const },
  // Superadmin extra — feature toggles + platform config
  { label: "Module Toggles",     to: "/app/superadmin", systemRoles: ["superadmin"], icon: <LayoutGrid className="h-4 w-4" /> },
  { label: "Report Builder",     to: "/app/superadmin/report-builder", systemRoles: ["superadmin"], icon: <FileText className="h-4 w-4" /> },
  { label: "Platform Config",    to: "/app/superadmin?section=config", systemRoles: ["superadmin"], icon: <Settings className="h-4 w-4" /> },
  { label: "Llogaria",           to: "/app/account",            icon: <UserCog className="h-4 w-4" /> },
];

export const SUPERADMIN_PLATFORM_NAV: NavItem[] = [
  { label: "Menaxhimi i Platformes", to: "/app/superadmin", icon: <Shield className="h-4 w-4" /> },
  { label: "Verifikimi i Mjekëve", to: "/app/superadmin/verify-doctors", badgeKey: "admin_approvals", icon: <ShieldCheck className="h-4 w-4" /> },
  { label: "Klinikat", to: "/app/superadmin/businesses", badgeKey: "businesses", icon: <Building2 className="h-4 w-4" /> },
  { label: "Moderimi i Blogut", to: "/app/superadmin/posts", icon: <FileSearch className="h-4 w-4" /> },
  { label: "Menaxhimi i Reklamave", to: "/app/superadmin/ads", icon: <FileImage className="h-4 w-4" /> },
  { label: "Aprovimet e Moduleve", to: "/app/superadmin?section=service-hub", badgeKey: "superadmin_pending", icon: <LayoutGrid className="h-4 w-4" /> },
  { label: "Service Hub", to: "/app/superadmin?section=service-hub", badgeKey: "superadmin_pending", icon: <LayoutGrid className="h-4 w-4" /> },
  { label: "Lista Globale e Perdoruesve", to: "/app/superadmin/users", icon: <UserCheck className="h-4 w-4" /> },
  { label: "Profilet", to: "/app/superadmin/profiles", icon: <UserCircle2 className="h-4 w-4" /> },
  { label: "Qytetet", to: "/app/superadmin/cities", icon: <MapPin className="h-4 w-4" /> },
  { label: "Siguria Globale", to: "/app/superadmin/security", badgeKey: "security_events", icon: <Lock className="h-4 w-4" /> },
  { label: "Analytics Global", to: "/app/superadmin/analytics", minLevel: 2, icon: <BarChart2 className="h-4 w-4" /> },
  { label: "Faturimi Global", to: "/app/superadmin/billing", badgeKey: "billing_invoices", icon: <CreditCard className="h-4 w-4" /> },
  { label: "Module Toggles", to: "/app/superadmin?section=modules", icon: <LayoutGrid className="h-4 w-4" /> },
  { label: "Report Builder", to: "/app/superadmin/report-builder", icon: <FileText className="h-4 w-4" /> },
  { label: "Llogaria", to: "/app/account", icon: <UserCog className="h-4 w-4" /> },
];

export const ADMIN_TENANT_NAV: NavItem[] = [
  { label: "Klinika Ime", to: "/app/business/manage", badgeKey: "staff_requests", icon: <Building2 className="h-4 w-4" /> },
  { label: "Moderimi", to: "/app/admin/moderation", icon: <FileText className="h-4 w-4" /> },
  { label: "Stafi", to: "/app/business/manage?tab=staff", badgeKey: "staff_requests", icon: <Users className="h-4 w-4" /> },
  { label: "Faturat e Klinikës", to: "/app/business/billing", badgeKey: "billing_invoices", icon: <Receipt className="h-4 w-4" />, businessRoles: ["owner"] },
  { label: "Llogaria", to: "/app/account", icon: <UserCog className="h-4 w-4" /> },
];

// ═══════════════════════════════════════════════════════════════════
// Zgjedhja e array-it sipas systemRole — logjika qendrore
// ═══════════════════════════════════════════════════════════════════
export const getVisibleNavItems = (
  systemRole:   SystemRole,
  profileRole:  ProfileRole | null,
  businessRole: BusinessRole | null,
  capabilities?: CapabilitiesDto,
  pathname?: string,
  currentLevel: number = 3,
  activeFunctionalAccountType?: string | null,
): NavItem[] => {
  const isBusinessPath = pathname
    ? HYBRID_BUSINESS_PATHS.some(p => pathname.startsWith(p))
    : false;
  const isClinicalPath =
    pathname?.startsWith("/app/professional") === true ||
    (pathname?.startsWith("/app") === true && !isBusinessPath);
  const isHybridClinicianOwner =
    businessRole === "owner" &&
    (profileRole === "doctor" || profileRole === "specialist");

  if (isHybridClinicianOwner && isClinicalPath) {
    return CLINICIAN_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  if (businessRole === "owner") {
    return OWNER_WORKSPACE_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  // 1. Teknician → nav i izoluar me 5 items laboratorike
  if (profileRole === "student") {
    return STUDENT_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  if (profileRole === "technician") {
    return TECHNICIAN_WORKBENCH_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  if (profileRole === "doctor" || profileRole === "specialist") {
    return DOCTOR_SPECIALIST_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  // 2. Zgjedh bazën sipas systemRole
  if (activeFunctionalAccountType === "basic" || (systemRole === "patient" && profileRole === null)) {
    return BASIC_NAV.filter(item =>
      _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
    );
  }

  const base: NavItem[] =
    systemRole === "superadmin" ? SUPERADMIN_PLATFORM_NAV :
    systemRole === "admin" ? ADMIN_TENANT_NAV :
    PATIENT_NAV;

  // 3. Filtro sipas moduleKey + sub-role restrictions
  return base.filter(item =>
    _isVisible(item, systemRole, profileRole, businessRole, capabilities, currentLevel),
  );
};

const _isVisible = (
  item:         NavItem,
  systemRole:   SystemRole,
  profileRole:  ProfileRole | null,
  businessRole: BusinessRole | null,
  capabilities?: CapabilitiesDto,
  currentLevel: number = 3,
): boolean => {
  const levelAllowed = systemRole === "superadmin" || !item.minLevel || currentLevel >= item.minLevel;

  const moduleEnabled =
    !item.moduleKey ||
    !capabilities ||
    capabilities.modules[item.moduleKey]?.enabled === true;

  const systemMatch =
    !item.systemRoles || item.systemRoles.includes(systemRole);

  const profileMatch =
    !item.profileRoles ||
    (profileRole !== null && item.profileRoles.includes(profileRole));

  const businessMatch =
    !item.businessRoles ||
    (businessRole !== null && item.businessRoles.includes(businessRole));

  return levelAllowed && moduleEnabled && systemMatch && profileMatch && businessMatch;
};
